import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import DraggableDialogPaper from './DraggableDialogPaper';

const PurchaseOrderAutoFillModal = ({ open, onClose, invoice, invoiceItems, userId, supabase, invoiceStore, poPriorityMode = false }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [pendingDetails, setPendingDetails] = useState([]);
  const [mappingResult, setMappingResult] = useState([]);
  const [hasExistingMapping, setHasExistingMapping] = useState(false);
  const [error, setError] = useState('');
  const [syncingOnDemand, setSyncingOnDemand] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const cancelSyncPollingRef = useRef(false);

  useEffect(() => () => {
    cancelSyncPollingRef.current = true;
  }, []);

  const handleCloseModal = () => {
    cancelSyncPollingRef.current = true;
    setSyncingOnDemand(false);
    onClose();
  };

  useEffect(() => {
    if (open && invoice?.num_identificacion && userId) {
      if (invoice.purchase_order_mapping && Array.isArray(invoice.purchase_order_mapping) && invoice.purchase_order_mapping.length > 0) {
        setMappingResult(invoice.purchase_order_mapping);
        setHasExistingMapping(true);
      } else {
        setMappingResult([]);
        setHasExistingMapping(false);
      }
      cancelSyncPollingRef.current = false;
      setSyncMessage('');
      loadPendingOrders();
    }
  }, [open, invoice, userId]);

  const loadPendingOrders = async (forceRecalculate = false) => {
    setLoading(true);
    setError('');
    try {
      // Load PO Headers
      const { data: headersData, error: headersError } = await supabase
        .from('pending_purchase_orders')
        .select('*')
        .eq('tenant_id', userId)
        .eq('id_n', invoice.num_identificacion)
        .order('fecha', { ascending: true }); // Oldest first!

      if (headersError) throw headersError;

      // Load PO Details
      let detailsData = [];
      if (headersData && headersData.length > 0) {
        const orderIds = headersData.map(h => h.id);
        const { data: details, error: detailsError } = await supabase
          .from('pending_purchase_order_details')
          .select('*')
          .in('order_id', orderIds)
          .gt('faltante', 0); // Only pending items

        if (detailsError) throw detailsError;
        detailsData = details || [];
      }

      setPendingOrders(headersData || []);
      setPendingDetails(detailsData);
      
      // If there's no existing mapping, calculate it right away
      if (forceRecalculate || !invoice.purchase_order_mapping || !Array.isArray(invoice.purchase_order_mapping) || invoice.purchase_order_mapping.length === 0) {
        if (forceRecalculate) setHasExistingMapping(false);
        calculateAutoFill(headersData || [], detailsData);
      }

    } catch (err) {
      console.error('Error loading pending purchase orders:', err);
      setError('Error al cargar órdenes de compra pendientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncSupplierOrders = async () => {
    if (!invoice?.id || !userId || syncingOnDemand) return;

    setSyncingOnDemand(true);
    setError('');
    setSyncMessage('Enviando solicitud a ServicioSAIDB...');
    cancelSyncPollingRef.current = false;

    try {
      let requestedBy = null;
      try {
        const storedUser = JSON.parse(localStorage.getItem('invoice_user') || 'null');
        requestedBy = storedUser?.sub_user_id || null;
      } catch {
        requestedBy = null;
      }

      const { data: requestData, error: requestError } = await supabase
        .rpc('request_invoice_po_sync', {
          p_invoice_id: invoice.id,
          p_requested_by: requestedBy
        });
      if (requestError) throw requestError;

      const request = Array.isArray(requestData) ? requestData[0] : requestData;
      if (!request?.request_id) throw new Error('No se recibió el identificador de la solicitud');

      const deadline = Date.now() + 90000;
      while (!cancelSyncPollingRef.current && Date.now() < deadline) {
        const { data: statusData, error: statusError } = await supabase
          .rpc('get_invoice_po_sync_request', { p_request_id: request.request_id });
        if (statusError) throw statusError;

        const status = Array.isArray(statusData) ? statusData[0] : statusData;
        if (!status) throw new Error('La solicitud de sincronización ya no está disponible');

        if (status.request_status === 'QUEUED') {
          setSyncMessage('Solicitud en cola. Esperando ServicioSAIDB...');
        } else if (status.request_status === 'PROCESSING') {
          setSyncMessage('Consultando las órdenes del proveedor en el ERP...');
        } else if (status.request_status === 'COMPLETED') {
          setSyncMessage(
            `Sincronización completada: ${status.orders_found} OC y ${status.details_found} líneas pendientes.`
          );
          await loadPendingOrders(true);
          return;
        } else if (status.request_status === 'NO_ORDERS') {
          setSyncMessage('El ERP no tiene órdenes de compra pendientes para este proveedor.');
          await loadPendingOrders(true);
          return;
        } else if (status.request_status === 'ERROR') {
          throw new Error(status.error_message || 'ServicioSAIDB no pudo sincronizar las órdenes');
        }

        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      if (!cancelSyncPollingRef.current) {
        throw new Error('ServicioSAIDB no respondió dentro de 90 segundos. La solicitud seguirá en cola.');
      }
    } catch (err) {
      console.error('Error synchronizing supplier purchase orders:', err);
      setError(err.message || 'Error al sincronizar las órdenes del proveedor');
      setSyncMessage('');
    } finally {
      if (!cancelSyncPollingRef.current) setSyncingOnDemand(false);
    }
  };

  const calculateAutoFill = async (headers, details) => {
    if (!headers.length || !details.length) {
      setMappingResult([]);
      return;
    }

    // We need the item_code for each invoice item to match with PO details
    // If provider_item_code is filled, we can use it. 
    // Otherwise, we need to fetch the item_code from invoice_products using product_id
    
    let itemsWithCodes = [...invoiceItems];
    
    // Fetch product codes for items that only have product_id
    const productIdsToFetch = itemsWithCodes
      .filter(i => i.product_id)
      .map(i => i.product_id);
      
    if (productIdsToFetch.length > 0) {
      const { data: products } = await supabase
        .from('invoice_products')
        .select('id, item_code')
        .in('id', productIdsToFetch);
        
      if (products) {
        const productMap = {};
        products.forEach(p => productMap[p.id] = p.item_code);
        
        itemsWithCodes = itemsWithCodes.map(item => ({
          ...item,
          computed_item_code: productMap[item.product_id] || item.provider_item_code || ''
        }));
      }
    } else {
      itemsWithCodes = itemsWithCodes.map(item => ({
        ...item,
        computed_item_code: item.provider_item_code || ''
      }));
    }

    // Reuse the latest manual correction made for the same supplier and
    // provider code. This is more reliable than description similarity for
    // presentations such as POTE, LATICA, CAJA X 12 or CAJA X 48.
    const learnedProductCodeByProviderCode = {};
    const providerCodes = [...new Set(
      itemsWithCodes
        .map(item => String(item.provider_item_code || '').trim())
        .filter(Boolean)
    )];

    if (providerCodes.length > 0 && invoice?.num_identificacion) {
      const { data: supplierInvoices, error: supplierInvoicesError } = await supabase
        .from('invoices')
        .select('id')
        .eq('user_id', userId)
        .eq('num_identificacion', invoice.num_identificacion)
        .order('id', { ascending: false })
        .limit(200);

      if (!supplierInvoicesError && supplierInvoices?.length > 0) {
        const { data: manualMatches, error: manualMatchesError } = await supabase
          .from('invoice_items')
          .select('id, provider_item_code, product_id')
          .in('invoice_id', supplierInvoices.map(currentInvoice => currentInvoice.id))
          .in('provider_item_code', providerCodes)
          .eq('product_match_manual', true)
          .not('product_id', 'is', null)
          .order('id', { ascending: false })
          .limit(2000);

        if (!manualMatchesError && manualMatches?.length > 0) {
          const learnedProductIds = [...new Set(manualMatches.map(match => match.product_id))];
          const { data: learnedProducts } = await supabase
            .from('invoice_products')
            .select('id, item_code')
            .eq('user_id', userId)
            .in('id', learnedProductIds);

          const learnedCodeByProductId = {};
          (learnedProducts || []).forEach(product => {
            learnedCodeByProductId[String(product.id)] = product.item_code;
          });

          manualMatches.forEach(match => {
            const providerCode = String(match.provider_item_code || '').trim();
            const productCode = learnedCodeByProductId[String(match.product_id)];
            if (providerCode && productCode && !learnedProductCodeByProviderCode[providerCode]) {
              learnedProductCodeByProviderCode[providerCode] = productCode;
            }
          });
        }
      }
    }

    // Deep copy details to keep track of remaining faltante during algorithm
    const workingDetails = JSON.parse(JSON.stringify(details));
    const results = [];

    const assignFromDetails = (invItem, candidateDetails, matchMetadata = {}) => {
      let remainingQtyToAssign = parseFloat(invItem.quantity) || 0;
      if (remainingQtyToAssign <= 0) return;

      const orderedDetails = candidateDetails.sort((a, b) => {
        const poA = headers.find(h => h.id === a.order_id);
        const poB = headers.find(h => h.id === b.order_id);
        const dateA = poA ? new Date(poA.fecha).getTime() : 0;
        const dateB = poB ? new Date(poB.fecha).getTime() : 0;
        return dateA - dateB;
      });

      for (const poDet of orderedDetails) {
        if (remainingQtyToAssign <= 0) break;

        const takeQty = poPriorityMode
          ? poDet.faltante
          : Math.min(remainingQtyToAssign, poDet.faltante);
        results.push({
          invoice_item_id: invItem.id,
          invoice_item_description: invItem.description,
          product_code: poDet.item,
          po_id: poDet.order_id,
          po_tipo: poDet.tipo,
          po_number: poDet.number,
          po_conteo: poDet.conteo,
          po_location: poDet.location,
          po_vat_rate: parseFloat(poDet.porc_iva) || 0,
          assigned_qty: takeQty,
          original_qty_in_invoice: parseFloat(invItem.quantity) || 0,
          original_tax_in_invoice: parseFloat(invItem.tax) || 0,
          ...matchMetadata
        });

        poDet.faltante -= takeQty;
        remainingQtyToAssign -= takeQty;
      }
    };

    // Algorithm: exact code first; description similarity only inside this supplier's pending POs.
    for (const invItem of itemsWithCodes) {
      const providerCode = String(invItem.provider_item_code || '').trim();
      const learnedProductCode = learnedProductCodeByProviderCode[providerCode];
      const learnedPoDetails = workingDetails
        .filter(detail => learnedProductCode && detail.item === learnedProductCode && detail.faltante > 0);

      if (learnedPoDetails.length > 0) {
        assignFromDetails(invItem, learnedPoDetails, {
          match_method: 'learned_manual_match',
          similarity_score: 1
        });
        continue;
      }

      const matchingPoDetails = workingDetails
        .filter(d => invItem.computed_item_code && d.item === invItem.computed_item_code && d.faltante > 0);

      if (matchingPoDetails.length > 0) {
        assignFromDetails(invItem, matchingPoDetails, { match_method: 'exact_code', similarity_score: 1 });
        continue;
      }

      if (!invItem.description) continue;

      const { data: similarDetails, error: similarityError } = await supabase
        .rpc('find_similar_po_details', {
          p_description: invItem.description,
          p_user_id: userId,
          p_supplier_nit: invoice.num_identificacion,
          p_limit: 25,
          p_similarity_threshold: 0.30
        });

      if (similarityError || !Array.isArray(similarDetails) || similarDetails.length === 0) {
        if (similarityError) console.error('Error matching invoice item against pending POs:', similarityError);
        continue;
      }

      const availableCandidates = similarDetails.filter(candidate =>
        workingDetails.some(detail => detail.id === candidate.detail_id && detail.faltante > 0)
      );
      if (availableCandidates.length === 0) continue;

      // Several pending OCs may contain the same product. Ambiguity is evaluated
      // between distinct product codes, not between repeated lines of one product.
      const candidatesByCode = [];
      const seenCodes = new Set();
      availableCandidates.forEach(candidate => {
        if (!seenCodes.has(candidate.item)) {
          seenCodes.add(candidate.item);
          candidatesByCode.push(candidate);
        }
      });

      const bestScore = Number(candidatesByCode[0].similarity_score) || 0;
      const secondScore = candidatesByCode.length > 1
        ? Number(candidatesByCode[1].similarity_score) || 0
        : null;

      // Avoid automatically selecting two descriptions that are effectively tied.
      if (secondScore !== null && bestScore - secondScore < 0.10) continue;

      const bestCode = candidatesByCode[0].item;
      const similarWorkingDetails = workingDetails.filter(
        detail => detail.item === bestCode && detail.faltante > 0
      );
      assignFromDetails(invItem, similarWorkingDetails, {
        match_method: 'po_description_similarity',
        similarity_score: bestScore
      });
    }

    setMappingResult(results);
  };

  const handleSave = async () => {
    if (!invoice?.id || !userId) return;
    
    setSaving(true);
    try {
      const mappingPayload = mappingResult.length > 0
        ? mappingResult.map(mapping => {
            const item = invoiceItems.find(currentItem => String(currentItem.id) === String(mapping.invoice_item_id));
            const detail = pendingDetails.find(currentDetail => (
              (String(currentDetail.order_id) === String(mapping.po_id)
                || String(currentDetail.number) === String(mapping.po_number))
              && String(currentDetail.conteo) === String(mapping.po_conteo)
              && (!mapping.product_code || String(currentDetail.item) === String(mapping.product_code))
            ));
            const poVatRate = parseFloat(detail?.porc_iva ?? mapping.po_vat_rate) || 0;
            const poUnitCost = parseFloat(detail?.cost ?? mapping.po_unit_cost) || 0;
            const assignedQty = parseFloat(mapping.assigned_qty) || 0;
            const poLineSubtotal = assignedQty * poUnitCost;
            return {
              ...mapping,
              po_vat_rate: poVatRate,
              po_unit_cost: poUnitCost,
              original_line_subtotal: poPriorityMode
                ? poLineSubtotal
                : mapping.original_line_subtotal,
              original_tax_in_invoice: poPriorityMode
                ? poLineSubtotal * (poVatRate / 100)
                : (Number.isFinite(parseFloat(mapping.original_tax_in_invoice))
                    ? parseFloat(mapping.original_tax_in_invoice)
                    : (parseFloat(item?.tax) || 0))
            };
          })
        : null;
      
      const { error: updateError } = await supabase
        .from('invoices')
        .update({
          purchase_order_mapping: mappingPayload,
          ...(poPriorityMode ? { proration_amount: null, freight_provider_nit: null } : {})
        })
        .eq('id', invoice.id);

      if (updateError) throw updateError;

      // Update warehouse_code on invoice_items
      if (mappingPayload?.length > 0) {
        const itemsToUpdate = {};
        mappingPayload.forEach(m => {
          if (m.po_location && !itemsToUpdate[m.invoice_item_id]) {
            itemsToUpdate[m.invoice_item_id] = m.po_location;
          }
        });

        const productCodesByItem = {};
        mappingPayload.forEach(m => {
          if (m.product_code && !productCodesByItem[m.invoice_item_id]) {
            productCodesByItem[m.invoice_item_id] = m.product_code;
          }
        });

        const productCodes = [...new Set(Object.values(productCodesByItem))];
        const { data: resolvedProducts, error: productsError } = productCodes.length > 0
          ? await supabase
              .from('invoice_products')
              .select('id, item_code')
              .eq('user_id', userId)
              .in('item_code', productCodes)
              .in('sync_status', ['SINCRONIZADO', 'SYNCED'])
          : { data: [], error: null };

        if (productsError) throw productsError;

        const productIdByCode = {};
        (resolvedProducts || []).forEach(product => {
          productIdByCode[product.item_code] = product.id;
        });

        const itemIds = poPriorityMode
          ? [...new Set(invoiceItems.map(item => String(item.id)))]
          : [...new Set(mappingPayload.map(mapping => String(mapping.invoice_item_id)))];
        const strictTaxByItemId = {};
        const strictValuesByItemId = {};
        if (poPriorityMode) {
          mappingPayload.forEach(mapping => {
            const itemId = String(mapping.invoice_item_id);
            const assignedQty = parseFloat(mapping.assigned_qty) || 0;
            const unitCost = parseFloat(mapping.po_unit_cost) || 0;
            const lineSubtotal = assignedQty * unitCost;
            const lineTax = lineSubtotal * ((parseFloat(mapping.po_vat_rate) || 0) / 100);
            const current = strictValuesByItemId[itemId] || { quantity: 0, subtotal: 0, tax: 0 };
            current.quantity += assignedQty;
            current.subtotal += lineSubtotal;
            current.tax += lineTax;
            strictValuesByItemId[itemId] = current;
          });
        }
        const updatePromises = itemIds.map(async (itemId) => {
          const updates = {};
          if (itemsToUpdate[itemId]) updates.warehouse_code = itemsToUpdate[itemId];
          const productCode = productCodesByItem[itemId];
          if (productCode && productIdByCode[productCode]) {
            updates.product_id = productIdByCode[productCode];
            updates.product_match_manual = false;
          }
          if (poPriorityMode) {
            const strictValues = strictValuesByItemId[itemId];
            if (strictValues?.quantity > 0) {
              updates.quantity = strictValues.quantity;
              updates.unit_price = strictValues.subtotal / strictValues.quantity;
              updates.original_unit_price = null;
              updates.total_price = strictValues.subtotal;
              updates.tax = strictValues.tax;
              strictTaxByItemId[itemId] = strictValues.tax;
            } else {
              const currentItem = invoiceItems.find(item => String(item.id) === itemId);
              const originalUnitPrice = parseFloat(currentItem?.original_unit_price);
              if (Number.isFinite(originalUnitPrice)) {
                const currentQty = parseFloat(currentItem?.quantity) || 0;
                updates.unit_price = originalUnitPrice;
                updates.original_unit_price = null;
                updates.total_price = originalUnitPrice * currentQty;
              }
            }
          }
          if (Object.keys(updates).length === 0) return;

          const { error: itemUpdateError } = await supabase
            .from('invoice_items')
            .update(updates)
            .eq('id', itemId);
          if (itemUpdateError) throw itemUpdateError;
        });
        await Promise.all(updatePromises);

        if (poPriorityMode) {
          const nextSubtotal = invoiceItems.reduce((subtotal, item) => (
            subtotal + (strictValuesByItemId[String(item.id)]?.subtotal
              ?? ((parseFloat(item.quantity) || 0)
                * (Number.isFinite(parseFloat(item.original_unit_price))
                  ? parseFloat(item.original_unit_price)
                  : (parseFloat(item.unit_price) || 0))))
          ), 0);
          const nextTax = invoiceItems.reduce((taxTotal, item) => (
            taxTotal + (Object.prototype.hasOwnProperty.call(strictTaxByItemId, String(item.id))
              ? strictTaxByItemId[String(item.id)]
              : (parseFloat(item.tax) || 0))
          ), 0);
          const nextInc = invoiceItems.reduce(
            (incTotal, item) => incTotal + (parseFloat(item.impuesto_consumo) || 0),
            0
          );
          const nextTotal = nextSubtotal + nextTax + nextInc - (parseFloat(invoice.discount) || 0);
          const { error: totalsError } = await supabase
            .from('invoices')
            .update({ subtotal: nextSubtotal, tax: nextTax, total: nextTotal })
            .eq('id', invoice.id)
            .eq('user_id', userId);
          if (totalsError) throw totalsError;
        }
      }
      
      if (invoiceStore?.getState()?.updateInvoice) {
        await invoiceStore.getState().updateInvoice(invoice.id);
      }
      
      handleCloseModal();
    } catch (err) {
      console.error('Error saving PO mapping:', err);
      setError('Error al guardar el cruce de órdenes de compra');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseModal} maxWidth="md" fullWidth PaperComponent={DraggableDialogPaper}>
      <DialogTitle className="draggable-dialog-title" sx={{ cursor: 'move' }}>
        Autocompletar con Órdenes de Compra
      </DialogTitle>
      <DialogContent dividers>
        {syncMessage && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: syncingOnDemand ? 'info.lighter' : 'success.lighter',
              border: '1px solid',
              borderColor: syncingOnDemand ? 'info.main' : 'success.main',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            {syncingOnDemand && <CircularProgress size={18} />}
            <Typography variant="body2">{syncMessage}</Typography>
          </Box>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} variant="fullWidth">
                <Tab label="Cruce Automático" />
                <Tab label="Órdenes Pendientes" />
              </Tabs>
            </Box>
            
            <Box sx={{ display: tabIndex === 0 ? 'block' : 'none' }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Proveedor: <strong>{invoice.billing_name}</strong> ({invoice.num_identificacion})
              </Typography>
              
              <Typography variant="h6" sx={{ fontSize: '1rem', mb: 1 }}>
                Órdenes Pendientes Encontradas: {pendingOrders.length}
              </Typography>
              
              {pendingOrders.length === 0 ? (
                <Typography variant="body2" color="warning.main">
                  No se encontraron órdenes de compra pendientes para este proveedor.
                </Typography>
              ) : (
                <>
                  {hasExistingMapping ? (
                    <Typography variant="body2" sx={{ mb: 2, color: 'success.main', fontWeight: 'bold' }}>
                      Esta factura ya cuenta con un cruce de órdenes guardado. Puedes revisarlo a continuación o forzar un recálculo si hubo cambios en los ítems:
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      El sistema ha distribuido automáticamente las cantidades de la factura actual llenando las OCs desde la más antigua a la más nueva. Revise el cruce a continuación:
                    </Typography>
                  )}
                  
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Ítem Factura</TableCell>
                          <TableCell>Código Prod.</TableCell>
                          <TableCell>Documento OC</TableCell>
                          <TableCell>Línea OC</TableCell>
                          <TableCell align="right">Cant. Asignada</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mappingResult.length > 0 ? (
                          mappingResult.map((row, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{row.invoice_item_description}</TableCell>
                              <TableCell>{row.product_code}</TableCell>
                              <TableCell>
                                <Chip size="small" label={`${row.po_tipo} ${row.po_number}`} color="primary" variant="outlined" />
                              </TableCell>
                              <TableCell>{row.po_conteo}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{row.assigned_qty}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              Ningún ítem de la factura coincidió con los códigos de las OCs pendientes.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>

            <Box sx={{ display: tabIndex === 1 ? 'block' : 'none' }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Listado de todos los ítems pendientes en órdenes de compra para el proveedor <strong>{invoice.billing_name}</strong>.
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Orden</TableCell>
                      <TableCell>Código</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell align="right">Cant. Original</TableCell>
                      <TableCell align="right">Faltante</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingDetails.length > 0 ? (
                      pendingDetails.map((det, idx) => {
                        const order = pendingOrders.find(o => o.id === det.order_id);
                        return (
                          <TableRow key={idx}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {det.tipo} {det.number}
                              </Typography>
                              {order && (
                                <Typography variant="caption" color="textSecondary">
                                  {new Date(order.fecha).toLocaleDateString()}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{det.item}</TableCell>
                            <TableCell>{det.description}</TableCell>
                            <TableCell align="right">{det.qty}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{det.faltante}</TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No hay detalles pendientes.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
          <Button
            onClick={handleSyncSupplierOrders}
            disabled={loading || saving || syncingOnDemand}
            variant="outlined"
          >
            {syncingOnDemand ? 'Sincronizando OC...' : 'Sincronizar OC de este proveedor'}
          </Button>
          {pendingOrders.length > 0 && hasExistingMapping && (
            <Button 
              onClick={() => {
                setHasExistingMapping(false);
                calculateAutoFill(pendingOrders, pendingDetails);
              }} 
              disabled={saving} 
              color="warning"
            >
              Recalcular Cruce
            </Button>
          )}
          <Button onClick={handleCloseModal} disabled={saving}>Cancelar</Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSave} 
          disabled={loading || saving || pendingOrders.length === 0}
        >
          {saving ? 'Guardando...' : 'Confirmar Cruce'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseOrderAutoFillModal;
